import rest from "@/rest-client";
import { searchCategories } from "../task-settings";

/** TKP-119 — görev durumu/önceliği kayıtları (organizasyon bazında); TKP-168: kategoriler de. `fetch` organizasyon değişince yenilenir. */
export const taskSettings = {
  namespaced: true,
  state: { statuses: [], priorities: [], categories: [], types: [], options: { typeEnabled: false }, mail: null, mailDefaults: {}, canEdit: false, organizationId: null, isLoading: false, isUpdating: false, others: {} },
  getters: {
    statuses: (state) => state.statuses,
    priorities: (state) => state.priorities,
    categories: (state) => state.categories,
    types: (state) => state.types, // TKP-220
    typeEnabled: (state) => !!(state.options && state.options.typeEnabled), // TKP-224: kapalıysa görevde tip görünmez
    mail: (state) => state.mail,
    mailDefaults: (state) => state.mailDefaults,
    canEdit: (state) => state.canEdit,
    isLoading: (state) => state.isLoading,
    isUpdating: (state) => state.isUpdating,
  },
  mutations: {
    settings: (state, { statuses, priorities, categories, types, options, mail, mailDefaults, canEdit, organizationId }) => {
      state.statuses = statuses || [];
      state.priorities = priorities || [];
      state.categories = categories || [];
      state.types = types || []; // TKP-220
      state.options = { typeEnabled: !!(options && options.typeEnabled) }; // TKP-224
      state.mail = mail || null;
      state.mailDefaults = mailDefaults || {};
      state.canEdit = !!canEdit;
      state.organizationId = organizationId || null;
    },
    isLoading: (state, value) => (state.isLoading = value),
    isUpdating: (state, value) => (state.isUpdating = value),
    /** TKP-240 b.3: seçili olmayan organizasyonun (görevin kendi organizasyonu) kategori / tip / seçenekleri. */
    other: (state, { organizationId, categories, types, options }) => {
      state.others = { ...state.others, [organizationId]: { categories: categories || [], types: types || [], typeEnabled: !!(options && options.typeEnabled) } };
    },
  },
  actions: {
    async fetch({ commit, rootGetters }) {
      commit("isLoading", true);
      try {
        const response = await rest.get("/task-settings");
        commit("settings", { ...response.data, organizationId: rootGetters["organization/selected"]?._id });
        return response;
      } finally {
        commit("isLoading", false);
      }
    },
    /** Seçili organizasyon için henüz yüklenmediyse yükler (rozet/seçici/kanban açılışı). */
    async ensure({ state, dispatch, rootGetters }) {
      const current = rootGetters["organization/selected"]?._id;
      if (!current) return;
      if (state.organizationId === current && state.statuses.length) return;
      await dispatch("fetch").catch(() => undefined);
    },
    async createStatus({ commit, dispatch }, item) {
      commit("isUpdating", true);
      try { const r = await rest.post("/task-settings/status", { item }); await dispatch("fetch"); return r; } finally { commit("isUpdating", false); }
    },
    async updateStatus({ commit, dispatch }, { _id, ...item }) {
      commit("isUpdating", true);
      try { const r = await rest.put(`/task-settings/status/${_id}`, { item }); await dispatch("fetch"); return r; } finally { commit("isUpdating", false); }
    },
    async deleteStatus({ commit, dispatch }, _id) {
      commit("isUpdating", true);
      try { const r = await rest.delete(`/task-settings/status/${_id}`); await dispatch("fetch"); return r; } finally { commit("isUpdating", false); }
    },
    async createPriority({ commit, dispatch }, item) {
      commit("isUpdating", true);
      try { const r = await rest.post("/task-settings/priority", { item }); await dispatch("fetch"); return r; } finally { commit("isUpdating", false); }
    },
    async updatePriority({ commit, dispatch }, { _id, ...item }) {
      commit("isUpdating", true);
      try { const r = await rest.put(`/task-settings/priority/${_id}`, { item }); await dispatch("fetch"); return r; } finally { commit("isUpdating", false); }
    },
    async deletePriority({ commit, dispatch }, _id) {
      commit("isUpdating", true);
      try { const r = await rest.delete(`/task-settings/priority/${_id}`); await dispatch("fetch"); return r; } finally { commit("isUpdating", false); }
    },
    // TKP-168 — kategoriler
    async createCategory({ commit, dispatch }, item) {
      commit("isUpdating", true);
      try { const r = await rest.post("/task-settings/category", { item }); await dispatch("fetch"); return r; } finally { commit("isUpdating", false); }
    },
    async updateCategory({ commit, dispatch }, { _id, ...item }) {
      commit("isUpdating", true);
      try { const r = await rest.put(`/task-settings/category/${_id}`, { item }); await dispatch("fetch"); return r; } finally { commit("isUpdating", false); }
    },
    async deleteCategory({ commit, dispatch }, _id) {
      commit("isUpdating", true);
      try { const r = await rest.delete(`/task-settings/category/${_id}`); await dispatch("fetch"); return r; } finally { commit("isUpdating", false); }
    },
    // TKP-220 — e-posta ayarı ve görev tipleri
    async updateMail({ commit, dispatch }, item) {
      commit("isUpdating", true);
      try { const r = await rest.put("/task-settings/mail", { item }); await dispatch("fetch"); return r; } finally { commit("isUpdating", false); }
    },
    // TKP-224 — görev tipi aç/kapa
    async updateOptions({ commit, dispatch }, item) {
      commit("isUpdating", true);
      try { const r = await rest.put("/task-settings/options", { item }); await dispatch("fetch"); return r; } finally { commit("isUpdating", false); }
    },
    async createType({ commit, dispatch }, item) {
      commit("isUpdating", true);
      try { const r = await rest.post("/task-settings/type", { item }); await dispatch("fetch"); return r; } finally { commit("isUpdating", false); }
    },
    async updateType({ commit, dispatch }, { _id, ...item }) {
      commit("isUpdating", true);
      try { const r = await rest.put(`/task-settings/type/${_id}`, { item }); await dispatch("fetch"); return r; } finally { commit("isUpdating", false); }
    },
    async deleteType({ commit, dispatch }, _id) {
      commit("isUpdating", true);
      try { const r = await rest.delete(`/task-settings/type/${_id}`); await dispatch("fetch"); return r; } finally { commit("isUpdating", false); }
    },
    /** Görev formu: organizasyonun görev tipleri (yüklü değilse yükler), ada göre süzülmüş. */
    /**
     * TKP-240 b.3 — görev formu: kategori / tip / "tip açık mı" GÖREVİN organizasyonundan. Seçili organizasyon değilse (kullanıcı
     * panelinde tüm organizasyonların görevleri; başka organizasyonun görevini düzenleme) o organizasyonun ayarı ayrıca okunur —
     * eskiden seçili organizasyonunki (ya da hiçbiri) kullanılıyor, liste boş / yanlış geliyordu. Liste boşsa bir kez tazelenir.
     */
    async settingsFor({ state, dispatch, commit, rootGetters }, organizationId) {
      const selected = rootGetters["organization/selected"]?._id;
      if (!organizationId || organizationId === selected) {
        await dispatch("ensure");
        if (!state.categories.length) await dispatch("fetch").catch(() => undefined);
        return { categories: state.categories, types: state.types, typeEnabled: !!(state.options && state.options.typeEnabled) };
      }
      if (!state.others[organizationId]) {
        try {
          const response = await rest.get("/task-settings", { headers: { Organization: organizationId } });
          commit("other", { organizationId, ...response.data });
        } catch (error) {
          commit("other", { organizationId });
        }
      }
      return state.others[organizationId];
    },
    async searchTypes({ state, dispatch }, value) {
      if (value && typeof value === "object") return searchCategories((await dispatch("settingsFor", value.organization)).types, value.value); // TKP-240 b.3
      await dispatch("ensure");
      return searchCategories(state.types, value);
    },
    /** Görev formu / süzgeçler: organizasyonun kategorileri (yüklü değilse yükler), ada göre süzülmüş. */
    async searchCategories({ state, dispatch }, value) {
      if (value && typeof value === "object") return searchCategories((await dispatch("settingsFor", value.organization)).categories, value.value); // TKP-240 b.3
      await dispatch("ensure");
      return searchCategories(state.categories, value);
    },
  },
};
